import React, { ReactNode, useState } from 'react';
import {
  ClickAwayListener,
  Link,
  Tooltip,
  withStyles,
} from '@material-ui/core';

/**
 * The tooltip holds links, so it needs a light background - the default dark
 * one leaves the blue links unreadable.
 */
const LightTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[2],
  },
}))(Tooltip);

/** @public */
export type MoreInTooltipProps = {
  /** How many entries are behind the link. */
  count: number;
  /** The entries, shown in the tooltip. */
  children: ReactNode;
};

/**
 * `+N more`, and the rest of a list in a tooltip. It opens on a click rather
 * than on hover, which a touch screen does not have, and stays open until the
 * next click elsewhere so that the links in it can be reached.
 *
 * @public
 */
export function MoreInTooltip(props: MoreInTooltipProps) {
  const { count, children } = props;
  const [open, setOpen] = useState(false);
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div style={{ alignSelf: 'flex-start' }}>
        <LightTooltip
          interactive
          open={open}
          onClose={() => setOpen(false)}
          disableHoverListener
          disableFocusListener
          disableTouchListener
          title={<>{children}</>}
        >
          <Link
            component="button"
            variant="caption"
            onClick={() => setOpen(it => !it)}
          >
            +{count} more
          </Link>
        </LightTooltip>
      </div>
    </ClickAwayListener>
  );
}
